import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config/config";

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export default function AssessmentTake() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    document.title = "Assessment - Ample Hire";
    axios
      .get(`${baseUrl}/job-posting/api/public/assessment/${token}`)
      .then((r) => {
        setData(r.data);
        setSecondsLeft((r.data.timeLimitMinutes || 60) * 60);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!data || done) return;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(intervalRef.current); submit(true); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [data, done]);

  const submit = async (auto = false) => {
    if (submitting || done) return;
    if (!auto && !window.confirm("Submit your answers? You can't change them after.")) return;
    setSubmitting(true);
    try {
      const list = (data?.questions || []).map((_, i) => ({ questionIndex: i, answer: answers[i] ?? null }));
      const r = await axios.post(`${baseUrl}/job-posting/api/public/assessment/${token}/submit`, { answers: list });
      setDone(r.data);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  if (!data) return <div className="p-10 text-center text-slate-500">Assessment link invalid or expired.</div>;

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-slate-200 rounded-xl p-10 text-center">
          <div className="text-5xl mb-3">{done.passed ? "✓" : "✓"}</div>
          <h2 className="text-2xl font-semibold text-slate-900">Assessment submitted</h2>
          <p className="text-sm text-slate-500 mt-2">Thanks for completing the assessment.</p>
          {done.showScore && (
            <div className="mt-4 p-3 rounded bg-slate-50 text-sm text-slate-700">
              Score: <strong>{done.totalScore}/{done.maxScore}</strong> ({done.percentage}%)
              {done.passed != null && <div className="text-xs text-slate-500 mt-1">{done.passed ? "Passed ✓" : "Did not pass"}</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  const questions = data.questions || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 px-6 py-3 flex items-center justify-between">
        <div>
          <h1 className="font-semibold text-slate-900">{data.title}</h1>
          <div className="text-xs text-slate-500">{questions.length} questions</div>
        </div>
        <div className={`px-3 py-1 rounded font-mono text-sm ${secondsLeft < 60 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>
          {fmtTime(secondsLeft)}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-5">
        {questions.length === 0 ? (
          <div className="text-center text-slate-500 py-10">This assessment has no questions yet.</div>
        ) : (
          questions.map((q, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="text-xs text-slate-500 mb-2">Question {i + 1}</div>
              <div className="text-sm text-slate-900 mb-3 font-medium">{q.question || q.text || "Untitled question"}</div>
              {(q.options || []).map((opt, j) => (
                <label key={j} className="flex items-center gap-2 py-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name={`q${i}`}
                    checked={answers[i] === opt}
                    onChange={() => setAnswers({ ...answers, [i]: opt })}
                  />
                  <span className="text-sm text-slate-700">{opt}</span>
                </label>
              ))}
            </div>
          ))
        )}

        <div className="flex justify-end">
          <button
            onClick={() => submit(false)}
            disabled={submitting}
            className="px-5 py-2.5 rounded bg-vf-primary text-white font-medium disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit assessment"}
          </button>
        </div>
      </main>
    </div>
  );
}
